import { Badge, IconButton, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { PageHeaderActions } from "@/components/common/PageHeaderActions";
import { RecordCommentsButton } from "@/components/common/comments/RecordCommentsButton";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ResourceName } from "@/types";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { getResourceIcon } from "@/utils/icons";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { policyDisplayLabel, useStoplossClaimContext } from "../hooks/useStoplossClaimContext";

/** A record link that reads as text and turns primary on hover: primary colour is reserved for actions. */
const quietLink = {
  color: "inherit",
  textDecoration: "none",
  "&:hover": { color: "primary.main", textDecoration: "underline" },
} as const;

/**
 * Stop-loss claim page header (#7581): the claimant's name in the app's h1 with
 * the record type ("Medical claim" / "Rx claim") beside it so the page is not
 * mistaken for the claimant's at a glance, the policy tag as a link (AC3), a
 * read-only status chip, the client on the second line. The breadcrumb already
 * says Stop-Loss Claims, so the visible title does not repeat it (it truncated);
 * the long form is kept for the document title and the overflow tooltip. No
 * send-icon menu, no Approve, no aegle id (it is on Details).
 */
export const StoplossClaimHeader = ({ onOpenTasks, tasksOpen }: { onOpenTasks: () => void; tasksOpen: boolean }) => {
  const { claim, displayClaimantId, claimant, client, policy, openTaskCount } = useStoplossClaimContext();
  const Icon = getResourceIcon(ResourceName.STOPLOSS_CLAIMS);
  const TaskIcon = getResourceIcon(ResourceName.TASKS);
  const { canDelete } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const { canList: canListTasks } = useResourcePermissions(ResourceName.TASKS);

  const claimantName = claimant?.claimant_name || "Claimant";
  const typeWord = claim.stoploss_claim_type ? formatToTitleCase(claim.stoploss_claim_type) : "";
  const typeLabel = typeWord ? `${typeWord} stop-loss claim` : "Stop-loss claim";
  const policyLabel = policyDisplayLabel(policy);

  return (
    <PageHeader
      customTitle={`${claimantName} ${typeLabel}`}
      titleNode={
        <>
          {displayClaimantId ? (
            <Typography
              component={RouterLink}
              to={`/claimants/${displayClaimantId}/show`}
              variant="inherit"
              sx={quietLink}
            >
              {claimantName}
            </Typography>
          ) : (
            claimantName
          )}
          {typeWord ? (
            <Typography component="span" variant="inherit" color="text.secondary" sx={{ fontWeight: 400, ml: 1.5 }}>
              {typeWord} claim
            </Typography>
          ) : null}
        </>
      }
      customTitleTag={policyLabel || undefined}
      titleTagHref={policy ? `/policies/${policy.id}/show` : undefined}
      statusKey="claim_status"
      enableStatusEdit={false}
      subtitleNode={
        client ? (
          <>
            <Typography component={RouterLink} to={`/clients/${client.id}/show`} variant="inherit" sx={quietLink}>
              {client.client_legal_name}
            </Typography>
            {claim.merged_into_claimant_id_ref ? (
              <>
                {" "}
                <StatusChip label="From merged record" color={ColorType.Default} size={UIComponentSize.Small} />
              </>
            ) : null}
          </>
        ) : undefined
      }
      headerIcon={<Icon sx={{ fontSize: 28, color: "primary.main" }} />}
      rightContent={
        <PageHeaderActions actions={{ delete: canDelete }}>
          {!tasksOpen && canListTasks && (
            <Tooltip title={openTaskCount ? `${openTaskCount} open task${openTaskCount === 1 ? "" : "s"}` : "Tasks"}>
              <IconButton onClick={onOpenTasks} sx={{ color: "text.secondary" }} aria-label="Open tasks">
                <Badge badgeContent={openTaskCount ?? 0} color="primary" max={99}>
                  <TaskIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          <RecordCommentsButton subjectType={ResourceName.STOPLOSS_CLAIMS} subjectId={String(claim.id)} />
        </PageHeaderActions>
      }
    />
  );
};
